import React from 'react';
import ArrowExpand from '@uber/icons/arrow-expand-filled';
import Download from '@uber/icons/download-filled';
import Fullscreen from '@uber/icons/fullscreen-outlined';
import Minus from '@uber/icons/minus-filled';
import Plus from '@uber/icons/plus-filled';
import { mergeOverrides, styled } from 'baseui';
import { type ButtonOverrides, Button } from 'baseui/button';
import { ACCESSIBILITY_TYPE, PLACEMENT, StatefulTooltip } from 'baseui/tooltip';
import { Collapse, Draw, Ellipse, Rotate, Ruler, Select, Show, Undo } from './icons';
const Divider = styled('div', ({
  $theme
}) => ({
  borderTop: `2px solid ${$theme.colors.buttonSecondarySelectedFill}`,
  margin: '4px 0'
}));
function getButtonOverrides() {
  return {
    BaseButton: {
      style: ({
        $theme: {
          colors,
          sizing
        },
        $isSelected,
        $disabled
      }) => {
        return {
          backgroundColor: $isSelected ? colors.buttonSecondarySelectedFill : colors.backgroundSecondary,
          color: colors.contentSecondary,
          opacity: $disabled ? 0.2 : 1,
          width: sizing.scale1000,
          height: sizing.scale1000,
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'space-around',
          padding: '8px',
          margin: '4px 0',
          ':hover': {
            backgroundColor: colors.buttonSecondaryHover
          },
          ':focus': {
            backgroundColor: colors.buttonSecondaryActive
          }
        };
      }
    }
  };
}
type IconConfig = {
  type: string;
  disabled?: boolean;
  isSelected?: boolean;
  tooltip?: string;
};
type Props = {
  iconList: Array<IconConfig>;
  onIconClick: (type: string) => void;
  iconOverrides?: ButtonOverrides;
};
const TypeIconMap = {
  undo: Undo,
  ruler: Ruler,
  rotate: Rotate,
  select: Select,
  show: Show,
  boundingBox: Draw,
  zoomIn: Plus,
  zoomOut: Minus,
  fullscreen: Fullscreen,
  expand: ArrowExpand,
  collapse: Collapse,
  download: Download,
  boundingEllipse: Ellipse
};
const Icon = ({
  onIconClick,
  iconOverrides,
  iconConfig
}: {
  iconConfig: IconConfig;
  onIconClick: (type: string) => void;
  iconOverrides?: ButtonOverrides;
}) => {
  const IconComponent = TypeIconMap[iconConfig.type];
  const overrides = mergeOverrides(mergeOverrides(getButtonOverrides(), iconOverrides), {
    BaseButton: {
      props: {
        'data-testid': `btn-toolbar-icon-${iconConfig.type}`
      }
    }
  });
  return <StatefulTooltip content={() => iconConfig.tooltip !== undefined ? iconConfig.tooltip : iconConfig.type} onMouseEnterDelay={0} onMouseLeaveDelay={0} accessibilityType={ACCESSIBILITY_TYPE.tooltip} placement={PLACEMENT.right}>
      <Button onClick={() => onIconClick(iconConfig.type)} isSelected={iconConfig.isSelected} disabled={Boolean(iconConfig.disabled)} overrides={overrides}>
        <IconComponent />
      </Button>
    </StatefulTooltip>;
};
export const ToolBar = (props: Props) => {
  return <>
      {props.iconList.map((icon, index) => {
      switch (icon.type) {
        case 'divider':
          return <Divider key={index} />;
        default:
          return <Icon key={index} iconConfig={icon} onIconClick={props.onIconClick} iconOverrides={props.iconOverrides}></Icon>;
      }
    })}
    </>;
};